import { useEffect, useState } from 'react';
import {
  profileAPI,
  ProfileUpdateData,
  NotificationTimeRequest,
  NotificationPeriodRequest,
  NotificationPreferences
} from '../lib/api/profile';
import { useAuth } from '../contexts/AuthContext';
import { useSettingsStore } from '../store/settingsStore';

export function useProfile() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {
    setLanguage,
    setDarkMode,
    setNotifications,
    setNotificationPreferences
  } = useSettingsStore();

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const response = await profileAPI.getProfile(); // Necesitamos agregar este método

        if (response.success && response.data) {
          const userData = response.data;

          // Update settings
          if (userData.language) setLanguage(userData.language);
          if (userData.darkMode !== undefined) setDarkMode(userData.darkMode);
          if (userData.notifications !== undefined) setNotifications(userData.notifications);

          // Update notification preferences
          if (userData.notificationPreferences) {
            setNotificationPreferences(userData.notificationPreferences);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load profile');
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  const updateProfile = async (data: ProfileUpdateData) => {
    try {
      setIsLoading(true);
      setError(null);

      // Update settings store
      if (data.language) setLanguage(data.language);
      if (data.darkMode !== undefined) setDarkMode(data.darkMode);
      if (data.notifications !== undefined) setNotifications(data.notifications);

      const updatedUser = await profileAPI.updateProfile(data);

      // Update local state
      // setUser(updatedUser);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const setNotificationTime = async (period: 'morning' | 'evening', hour: number) => {
    try {
      setIsLoading(true);
      setError(null);

      const request: NotificationTimeRequest = { period, hour };
      const response = await profileAPI.setNotificationTime(request);

      if (response.success && response.data.notificationPreferences) {
        setNotificationPreferences(response.data.notificationPreferences);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set notification time');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const toggleNotificationPeriod = async (period: 'morning' | 'evening', enabled: boolean) => {
    try {
      setIsLoading(true);
      setError(null);

      const request: NotificationPeriodRequest = { period, enabled };
      const response = await profileAPI.toggleNotificationPeriod(request);

      if (response.success && response.data.notificationPreferences) {
        setNotificationPreferences(response.data.notificationPreferences);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle notification period');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const fetchNotificationPreferences = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await profileAPI.getNotificationPreferences();

      if (response.success && response.data) {
        setNotificationPreferences(response.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch notification preferences');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    updateProfile,
    setNotificationTime,
    toggleNotificationPeriod,
    fetchNotificationPreferences,
    isLoading,
    error
  };
}